'use client'
import { useState } from 'react'
import { motion, useMotionValueEvent, useTransform, type MotionValue } from 'framer-motion'
import { EASE } from '@/lib/constants/animations'
import type { MeasurementStep } from '@/lib/data/measurement'

type TimelineStepProps = {
  step: MeasurementStep
  progress: MotionValue<number>
  threshold: number
  nextThreshold: number
  isLast: boolean
  fa: boolean
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.2}
      className="w-4 h-4"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

export default function TimelineStep({
  step,
  progress,
  threshold,
  nextThreshold,
  isLast,
  fa,
}: TimelineStepProps) {
  // `active` flips when the shared progress sweeps past this step's threshold.
  const [active, setActive] = useState(() => progress.get() >= threshold)
  useMotionValueEvent(progress, 'change', (v) => setActive(v >= threshold))

  // The connector to the next step fills as progress travels between them.
  const connectorFill = useTransform(progress, [threshold, nextThreshold], [0, 1])

  return (
    <div className="flex items-start gap-4 sm:gap-5">
      {/* Node + dotted connector */}
      <div className="flex flex-col items-center self-stretch flex-shrink-0">
        <div className="relative w-9 h-9 flex-shrink-0">
          {/* Outline ring — always visible so the node never disappears */}
          <div
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: 'rgba(0,180,216,0.3)' }}
          />
          {/* Filled checkmark — fades + pops in on activation */}
          <motion.div
            className="absolute inset-0 rounded-full flex items-center justify-center text-white"
            style={{ background: 'linear-gradient(135deg,#0335b7,#00b4d8)' }}
            initial={false}
            animate={
              active
                ? {
                    opacity: 1,
                    scale: 1,
                    boxShadow: '0 0 0 4px rgba(0,180,216,0.1), 0 6px 18px -4px rgba(0,180,216,0.5)',
                  }
                : { opacity: 0, scale: 0.4, boxShadow: '0 0 0 0 rgba(0,180,216,0)' }
            }
            transition={{ duration: 0.4, ease: EASE }}
          >
            <CheckIcon />
          </motion.div>
        </div>

        {!isLast && (
          <div className="relative flex-1 w-0 my-2 min-h-[2rem]">
            {/* Dim base */}
            <div
              className="absolute inset-0 border-l-[3px] border-dotted"
              style={{ borderColor: 'rgba(0,180,216,0.18)' }}
            />
            {/* Bright fill that grows top-down with progress */}
            <motion.div
              className="absolute inset-0 border-l-[3px] border-dotted"
              style={{
                borderColor: 'rgba(0,180,216,0.7)',
                scaleY: connectorFill,
                transformOrigin: 'top',
              }}
            />
          </div>
        )}
      </div>

      {/* Text — full opacity always, never gated by the synced animation */}
      <div className={isLast ? '' : 'pb-8 lg:pb-10'}>
        <h3 className="font-display font-black text-base sm:text-lg text-white mb-1.5">
          {fa ? step.titleFA : step.titleEN}
        </h3>
        <p className="text-sm leading-loose" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {fa ? step.bodyFA : step.bodyEN}
        </p>
      </div>
    </div>
  )
}
